import React, { useState, useEffect, useCallback } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import CommercePolicyPanel from './components/CommercePolicyPanel';
import TopupSection from './components/TopupSection';
import SideHelpPanel from './components/SideHelpPanel';
import ProductNav from './components/ProductNav';
import { SCC_CLOUD_OPTIONS, SCC_WORKFORCE_EXTENSIONS, sccMonthlyTotal } from './config/sccPricing';

const PLAN_COLORS = {
  explorer:    '#6b7280',
  artisan:     '#10b981',
  architect:   '#8b5cf6',
  visionary:   '#f97316',
  creator_pro: '#0ea5e9',
  commercial:  '#dc2626',
};

const BILLING_TERMS = [
  { key: 'monthly', label: 'Monthly' },
  { key: '6month',  label: '6 Months' },
  { key: '12month', label: '12 Months' },
];

// Prices MUST match the Stripe Prices behind /v1/subscriptions/checkout, for the same
// reason the top-up table below is pinned: this table is what a customer reads before
// being charged. The previous values (6.99 / 16.99 / 27.99 / 44.99 / 84.99 and their
// term prices) were 43-50% BELOW what Stripe actually charged.
const PLAN_PRICES = {
  artisan:     { monthly: '$9.99',  '6month': '$59',  '12month': '$119'  },
  architect:   { monthly: '$25',    '6month': '$150', '12month': '$300'  },
  visionary:   { monthly: '$40',    '6month': '$240', '12month': '$480'  },
  creator_pro: { monthly: '$65',    '6month': '$390', '12month': '$780'  },
  commercial:  { monthly: '$130',   '6month': '$780', '12month': '$1560' },
};

const ALL_PLANS = [
  { slug: 'explorer',    name: 'Explorer',    specs: 10,   isExplorer: true,  description: 'Try VISINVOKE free — no credit card required. Access all systems. 10 credits total, no monthly reset.', benefit: null, specDiff: null },
  { slug: 'artisan',     name: 'Artisan',     specs: 80,   isExplorer: false, description: 'Perfect for solo creators and hobbyists. Full access to all VISINVOKE systems.', benefit: null, specDiff: null },
  { slug: 'architect',   name: 'Architect',   specs: 220,  isExplorer: false, description: 'Most popular — serious creators and regular use. Full access to all VISINVOKE systems.', benefit: 'Best for regular creators', specDiff: '+140 vs Artisan' },
  { slug: 'visionary',   name: 'Visionary',   specs: 420,  isExplorer: false, description: 'High-volume production and professional studios. Full access to all VISINVOKE systems.', benefit: 'Ideal for heavy creators and campaigns', specDiff: '+200 vs Architect' },
  { slug: 'creator_pro', name: 'Creator Pro', specs: 700,  isExplorer: false, description: 'Advanced creators stepping up to professional volume. Full access to all VISINVOKE systems.', benefit: 'Perfect for high-volume creators', specDiff: '+280 vs Visionary' },
  { slug: 'commercial',  name: 'Commercial',  specs: 1100, isExplorer: false, description: 'Enterprise-scale access with full commercial rights. All VISINVOKE systems included.', benefit: 'Designed for studios and professional use', specDiff: '+400 vs Creator Pro' },
];

// Prices MUST match the server-authoritative TOPUP_SPECS in backend/routes/stripe_webhook.py --
// that table is what Stripe actually charges. topup_120 was displayed as $59.99 while the server
// charged $60.00. Pinned by backend/tests/test_topup_price_display_parity.py.
const ADDITIONAL_CREDITS_INFO = [
  { key: 'topup_10',  specs: 10,  price: '$5.99'  },
  { key: 'topup_25',  specs: 25,  price: '$12.99' },
  { key: 'topup_60',  specs: 60,  price: '$29.99' },
  { key: 'topup_120', specs: 120, price: '$60.00' },
];

const COUNTRIES = [
  'Australia', 'New Zealand', 'United States', 'United Kingdom', 'Canada',
  'Ireland', 'Germany', 'France', 'Netherlands', 'Sweden', 'Norway',
  'Denmark', 'Finland', 'Switzerland', 'Austria', 'Belgium', 'Italy',
  'Spain', 'Portugal', 'Japan', 'South Korea', 'Singapore', 'India',
  'Brazil', 'Mexico', 'South Africa', 'Other',
];

const UPGRADE_LADDER = {
  artisan:     'architect',
  architect:   'visionary',
  visionary:   'creator_pro',
  creator_pro: 'commercial',
};

export default function Manage() {
  // Shared consent controller. Inert unless the SERVER refuses the checkout pending an
  // immediate-supply acknowledgement; then it renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [usage,          setUsage]          = useState(null);
  const [loadingUsage,   setLoadingUsage]   = useState(true);
  const [usageHistory,   setUsageHistory]   = useState([]);
  const [historyPage,    setHistoryPage]    = useState(1);
  const [historyTotal,   setHistoryTotal]   = useState(0);
  const [historyPages,   setHistoryPages]   = useState(1);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [changingPlan,   setChangingPlan]   = useState(null);
  const [planMessage,    setPlanMessage]    = useState('');
  const [actionLoading,  setActionLoading]  = useState(false);
  const [actionMessage,  setActionMessage]  = useState('');
  const [billingTerm,    setBillingTerm]    = useState('12month');

  const [autoTopupEnabled, setAutoTopupEnabled] = useState(false);
  const [autoTopupPack,    setAutoTopupPack]    = useState('topup_10');
  const [autoTopupSaving,  setAutoTopupSaving]  = useState(false);
  const [autoTopupMsg,     setAutoTopupMsg]     = useState('');

  const [profileForm, setProfileForm] = useState({
    first_name: '', last_name: '', company: '', phone: '', country: 'United States',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError,   setProfileError]   = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [billingPortalErr,     setBillingPortalErr]     = useState('');
  const [sccStaffLimit, setSccStaffLimit] = useState(10);
  const [sccCloudTier, setSccCloudTier] = useState('none');
  const [sccPlanSaving, setSccPlanSaving] = useState(false);
  const [sccPlanMessage, setSccPlanMessage] = useState('');

  useEffect(() => {
    const entitlements = user?.entitlements || {};
    setSccStaffLimit(Number(entitlements.service_command_centre_staff_limit || 10));
    setSccCloudTier(entitlements.service_command_centre_cloud_tier || 'none');
  }, [user]);

  const handleSccPlanChange = async () => {
    setSccPlanSaving(true);
    setSccPlanMessage('');
    try {
      const res = await API.post('/api/payments/update-service-command-centre', {
        staff_limit: Number(sccStaffLimit),
        cloud_tier: sccCloudTier,
      });
      setSccStaffLimit(Number(res.data?.staff_limit || sccStaffLimit));
      setSccCloudTier(res.data?.cloud_tier || sccCloudTier);
      setSccPlanMessage(`SCC plan updated. New monthly total: $${Number(res.data?.monthly_total_usd || 65).toFixed(2)} USD.`);
    } catch (e) {
      setSccPlanMessage(e.response?.data?.error || 'Could not update the SCC plan. Please try again.');
    } finally {
      setSccPlanSaving(false);
    }
  };

  const handleManageBilling = async () => {
    setBillingPortalLoading(true);
    setBillingPortalErr('');
    try {
      const res = await API.post('/api/payments/create-billing-portal-session', {
        return_url: window.location.href,
      });
      if (res.data?.portal_url) {
        window.location.href = res.data.portal_url;
      } else {
        setBillingPortalErr('Could not open billing portal. Please try again.');
      }
    } catch (e) {
      setBillingPortalErr(e.response?.data?.error || 'Could not open billing portal. Please try again.');
    } finally {
      setBillingPortalLoading(false);
    }
  };

  const hobbyPack   = usage?.hobby_pack || null;
  const isHobbyPack = !!hobbyPack;

  const TABS = isHobbyPack
    ? [
        { id: 'account',         label: 'Account Details' },
        { id: 'technician',      label: 'Technician' },
        { id: 'pack-details',    label: 'Pack Details' },
        { id: 'upgrade-plan',    label: 'Upgrade to Subscription' },
        { id: 'usage-history',   label: 'Usage History' },
        { id: 'billing-history', label: 'Billing History' },
      ]
    : [
        { id: 'account',         label: 'Account Details' },
        { id: 'technician',      label: 'Technician' },
        { id: 'change-plan',     label: 'Change Plan' },
        { id: 'top-up',          label: 'Additional Credits' },
        { id: 'usage-history',   label: 'Usage History' },
        { id: 'billing-history', label: 'Billing History' },
      ];

  const defaultTab = location.state?.activeTab || 'account';
  const [activeTab, setActiveTab] = useState(defaultTab);

  const loadUsage = useCallback(() => {
    setLoadingUsage(true);
    API.get('/v1/usage/current')
      .then(res => setUsage(res.data))
      .catch(() => null)
      .finally(() => setLoadingUsage(false));
  }, []);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name:  user.last_name  || '',
        company:    user.company    || '',
        phone:      user.phone      || '',
        country:    user.country    || 'United States',
      });
    }
  }, [user]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('visinvoke_auto_topup') || '{}');
      if (saved.enabled !== undefined) setAutoTopupEnabled(saved.enabled);
      if (saved.pack)                  setAutoTopupPack(saved.pack);
    } catch (_) {}
  }, []);

  const saveAutoTopup = () => {
    setAutoTopupSaving(true);
    setAutoTopupMsg('');
    try {
      localStorage.setItem('visinvoke_auto_topup', JSON.stringify({ enabled: autoTopupEnabled, pack: autoTopupPack }));
      setAutoTopupMsg('Auto additional credits preference saved.');
    } catch (_) {
      setAutoTopupMsg('Failed to save preference.');
    } finally {
      setAutoTopupSaving(false);
    }
  };

  const loadHistory = useCallback((page = 1) => {
    setLoadingHistory(true);
    API.get(`/v1/usage/history?page=${page}&per_page=10`)
      .then(r => {
        setUsageHistory(r.data.history || []);
        setHistoryTotal(r.data.total   || 0);
        setHistoryPages(r.data.pages   || 1);
        setHistoryPage(page);
      })
      .catch(() => setUsageHistory([]))
      .finally(() => setLoadingHistory(false));
  }, []);

  useEffect(() => {
    if (activeTab === 'usage-history') loadHistory(1);
  }, [activeTab, loadHistory]);

  useEffect(() => {
    if (activeTab === 'billing-history') {
      setLoadingBilling(true);
      Promise.all([
        API.get('/v1/subscriptions/history').catch(() => ({ data: { subscriptions: [] } })),
        API.get('/v1/topup/history').catch(() => ({ data: { topups: [] } })),
        API.get('/v1/hobby-packs/history').catch(() => ({ data: { packs: [] } })),
      ]).then(([subRes, topupRes, packRes]) => {
        const subs = (subRes.data.subscriptions || []).map(s => ({
          type: 'subscription', date: s.created_at,
          description: `${s.plan?.name || 'VISINVOKE Plan'} Subscription`, amount: null, status: s.status,
        }));
        const topups = (topupRes.data.topups || []).map(t => ({
          type: 'additional_credits', date: t.purchased_at,
          description: `Additional Credits (+${t.specs_added} credits)`, amount: t.amount_charged, status: 'completed',
        }));
        const packs = (packRes.data.packs || []).map(p => ({
          type: 'content_pack', date: p.purchased_at,
          description: `${p.pack_name} Content Pack`,
          amount: p.amount_charged, status: p.status,
        }));
        setBillingHistory([...subs, ...topups, ...packs].sort((a, b) => new Date(b.date) - new Date(a.date)));
      }).finally(() => setLoadingBilling(false));
    }
  }, [activeTab]);

  const handleChangePlan = async (plan, term) => {
    if (plan.slug === usage?.plan_slug) return;
    if (plan.slug === 'explorer') {
      setPlanMessage('To downgrade to Explorer (free), please cancel your subscription below.');
      return;
    }
    setChangingPlan(plan.slug);
    setPlanMessage('');
    try {
      const r = await consentCheckout.start(() => API.post('/v1/subscriptions/checkout', { plan_slug: plan.slug, billing_term: term }), { purchaseType: 'subscription', intendedSelector: plan.slug });
      // null means the shared controller is showing the acknowledgement instead of completing.
      if (r && r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        setPlanMessage('Checkout link not available. Please try again.');
      }
    } catch (err) {
      setPlanMessage(err.response?.data?.error || 'Failed to start plan change. Please try again.');
    } finally {
      setChangingPlan(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(
      'Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.'
    )) return;
    setActionLoading(true);
    setActionMessage('');
    try {
      await API.post('/v1/subscriptions/cancel');
      setActionMessage('Your subscription has been cancelled. Access continues until your period ends.');
      loadUsage();
    } catch (err) {
      setActionMessage(err.response?.data?.error || 'Failed to cancel subscription.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleProfileSave = async () => {
    setProfileError(''); setProfileSuccess('');
    if (!profileForm.first_name.trim()) { setProfileError('First name is required.'); return; }
    if (!profileForm.last_name.trim())  { setProfileError('Last name is required.'); return; }
    setProfileLoading(true);
    try {
      await API.patch('/v1/auth/profile', profileForm);
      setProfileSuccess('Account details updated successfully.');
    } catch (err) {
      setProfileError(err.response?.data?.error || 'Failed to save changes. Please try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  const specsUsed      = isHobbyPack ? (hobbyPack.specs_used || 0) : (usage?.used ?? 0);
  const specsLimit     = isHobbyPack ? hobbyPack.specs_purchased : (usage?.limit ?? 0);
  const specsRemaining = isHobbyPack ? hobbyPack.specs_remaining : Math.max(0, (usage?.limit ?? 0) - (usage?.used ?? 0));
  const progressPct    = specsLimit > 0 ? Math.min(100, Math.round((specsUsed / specsLimit) * 100)) : 0;
  const planColor      = isHobbyPack ? '#D4AF37' : (PLAN_COLORS[usage?.plan_slug] || '#8b5cf6');
  const barColor       = progressPct >= 100 ? '#dc2626' : progressPct >= 80 ? '#f97316' : planColor;
  const planName       = isHobbyPack ? `${hobbyPack.pack_name} Content Pack` : (usage?.plan_name || 'Explorer');
  const planStatus     = isHobbyPack ? 'One-Time Purchase' : (usage?.subscription_status || 'trial');
  const isExplorer     = !isHobbyPack && (usage?.plan_slug === 'explorer' || !usage?.plan_slug);

  const nextPlanSlug = UPGRADE_LADDER[usage?.plan_slug];
  const nextPlanInfo = nextPlanSlug ? ALL_PLANS.find(p => p.slug === nextPlanSlug) : null;

  return (
    <div style={s.page}>
      <GuardedCheckoutConsent checkout={consentCheckout} />
      <SideHelpPanel title="Manage Subscription">
        <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 20, overflowY: 'auto' }}>
          <div>
            <span style={hpm.heading}>What This Page Is</span>
            <p style={hpm.body}>This page allows you to manage your VISINVOKE subscription, update your account details, track your usage, and review billing activity.</p>
          </div>
          <div style={hpm.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hpm.heading}>Credits</span>
            <p style={hpm.body}>Credits are consumed when you generate images. The amount used depends on the quality tier and system type selected.</p>
          </div>
          <div style={hpm.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hpm.heading}>Additional Credits</span>
            <p style={hpm.body}>Purchase additional credits if you need more generations beyond your monthly allocation. One per billing cycle.</p>
          </div>
          <div style={hpm.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hpm.heading}>Tips</span>
            <ul style={hpm.list}>
              <li style={hpm.item}>Upgrade your plan before running large generation batches</li>
              <li style={hpm.item}>Use additional credits for short-term needs</li>
              <li style={hpm.item}>Keep your account details up to date for billing accuracy</li>
            </ul>
          </div>
        </div>
      </SideHelpPanel>

      <ProductNav />

      <div style={s.container}>

        <div style={s.pageHeader}>
          <div style={s.pageHeaderTop}>
            <div>
              <h1 style={s.pageTitle}>{isHobbyPack ? 'Manage Content Pack' : 'Manage Subscription'}</h1>
              <p style={s.pageSub}>View and manage your VISINVOKE plan, credits, and billing.</p>
            </div>
          </div>
        </div>

        {!loadingUsage && usage && (
          <div style={s.summaryStrip}>
            <div style={s.summaryItem}>
              <span style={s.summaryLabel}>Current Plan</span>
              <span style={{ ...s.summaryValue, color: planColor, fontWeight: 800 }}>{planName}</span>
            </div>
            <div style={s.summaryDivider} />
            <div style={s.summaryItem}>
              <span style={s.summaryLabel}>Credits Used</span>
              <span style={s.summaryValue}>{specsUsed} / {specsLimit}</span>
            </div>
            <div style={s.summaryDivider} />
            <div style={s.summaryItem}>
              <span style={s.summaryLabel}>Remaining</span>
              <span style={{ ...s.summaryValue, color: barColor }}>{specsRemaining}</span>
            </div>
            <div style={s.summaryDivider} />
            <div style={s.summaryItem}>
              <span style={s.summaryLabel}>Status</span>
              <span style={{ ...s.summaryValue, color: isHobbyPack ? '#D4AF37' : (planStatus === 'active' ? '#10b981' : '#f97316'), textTransform: 'capitalize' }}>
                {planStatus}
              </span>
            </div>
            {!isHobbyPack && (
              <>
                <div style={s.summaryDivider} />
                <div style={s.summaryItem}>
                  <span style={s.summaryLabel}>Additional Credits</span>
                  <span style={{ ...s.summaryValue, color: '#8b5cf6' }}>{usage?.topup_credits || 0}</span>
                </div>
              </>
            )}
            {isHobbyPack && (
              <>
                <div style={s.summaryDivider} />
                <div style={s.summaryItem}>
                  <span style={s.summaryLabel}>Additional Credits</span>
                  <span style={{ ...s.summaryValue, color: '#9ca3af' }}>Not available</span>
                </div>
              </>
            )}
            <div style={{ flex: 1 }} />
            <div style={s.barWrap}>
              <div style={s.barTrack}>
                <div style={{ ...s.barFill, width: `${progressPct}%`, background: barColor }} />
              </div>
              <span style={s.barLabel}>{progressPct}% used</span>
            </div>
          </div>
        )}

        {/* SUBSCRIPTION MANAGEMENT SECTION */}
        {!loadingUsage && usage && !isHobbyPack && !isExplorer && (
          <div style={s.billingMgmtSection}>
            <h3 style={s.billingMgmtTitle}>Subscription Management</h3>
            <div style={s.billingMgmtGrid}>
              <div style={s.billingMgmtItem}>
                <span style={s.billingMgmtLabel}>Current Plan</span>
                <span style={{ ...s.billingMgmtValue, color: planColor }}>{planName}</span>
              </div>
              <div style={s.billingMgmtDivider} />
              <div style={s.billingMgmtItem}>
                <span style={s.billingMgmtLabel}>Status</span>
                <span style={{
                  ...s.billingMgmtValue,
                  color: planStatus === 'active' ? '#10b981' : planStatus === 'past_due' ? '#f97316' : planStatus === 'cancelled' ? '#dc2626' : '#9ca3af',
                  textTransform: 'capitalize',
                }}>{planStatus}</span>
              </div>
              <div style={s.billingMgmtDivider} />
              <div style={s.billingMgmtItem}>
                <span style={s.billingMgmtLabel}>Cloud Sync</span>
                {(() => {
                  const ents = user?.entitlements || {};
                  const cs        = ents.combat_cloud_sync;
                  const csStatus  = ents.combat_cloud_sync_status;
                  const csPeriodEnd = ents.combat_cloud_sync_period_end;
                  if (!cs) return <span style={{ ...s.billingMgmtValue, color: '#9ca3af' }}>Not Active</span>;
                  if (csStatus === 'suspended') return <span style={{ ...s.billingMgmtValue, color: '#dc2626' }}>Suspended</span>;
                  if (csStatus === 'past_due')  return <span style={{ ...s.billingMgmtValue, color: '#f97316' }}>Past Due</span>;
                  if (csPeriodEnd && csStatus !== 'active') {
                    return <span style={{ ...s.billingMgmtValue, color: '#f97316', fontSize: 13 }}>
                      Active until {new Date(csPeriodEnd).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>;
                  }
                  return <span style={{ ...s.billingMgmtValue, color: '#10b981' }}>&#x2714; Active</span>;
                })()}
              </div>
              <div style={s.billingMgmtDivider} />
              <div style={s.billingMgmtItem}>
                <span style={s.billingMgmtLabel}>SCC</span>
                {(() => {
                  const ents = user?.entitlements || {};
                  const scc       = ents.service_command_centre_access;
                  const sccStatus = ents.service_command_centre_status;
                  if (!scc) return <span style={{ ...s.billingMgmtValue, color: '#9ca3af' }}>Not Active</span>;
                  if (sccStatus === 'suspended') return <span style={{ ...s.billingMgmtValue, color: '#dc2626' }}>Suspended</span>;
                  if (sccStatus === 'past_due')  return <span style={{ ...s.billingMgmtValue, color: '#f97316' }}>Past Due</span>;
                  return <span style={{ ...s.billingMgmtValue, color: '#10b981' }}>&#x2714; Active</span>;
                })()}
              </div>
              <div style={{ flex: 1 }} />
              <div style={s.billingMgmtActions}>
                {billingPortalErr && <span style={s.billingMgmtErr}>{billingPortalErr}</span>}
                <button
                  style={{ ...s.billingMgmtBtn, opacity: billingPortalLoading ? 0.6 : 1 }}
                  onClick={handleManageBilling}
                  disabled={billingPortalLoading}
                >
                  {billingPortalLoading ? 'Opening...' : 'Manage Billing'}
                </button>
              </div>
            </div>
          </div>
        )}

        {user?.entitlements?.service_command_centre_access && (
          <div style={s.billingMgmtSection}>
            <h3 style={s.billingMgmtTitle}>Service Command Centre Plan</h3>
            <div style={s.sccPlanGrid}>
              <label style={s.sccPlanField}>
                <span style={s.billingMgmtLabel}>Total staff allowance</span>
                <select value={sccStaffLimit} onChange={(event) => setSccStaffLimit(Number(event.target.value))} style={s.sccPlanSelect}>
                  <option value={10}>Up to 10 staff - included</option>
                  {SCC_WORKFORCE_EXTENSIONS.map((tier) => (
                    <option key={tier.key} value={tier.staffLimit}>{tier.title} - {tier.price}/mo ({tier.total} SCC total)</option>
                  ))}
                </select>
              </label>
              <label style={s.sccPlanField}>
                <span style={s.billingMgmtLabel}>Managed cloud</span>
                <select value={sccCloudTier} onChange={(event) => setSccCloudTier(event.target.value)} style={s.sccPlanSelect}>
                  <option value="none">No managed cloud</option>
                  {SCC_CLOUD_OPTIONS.map((tier) => (
                    <option key={tier.key} value={tier.key}>{tier.title} - {tier.price}/mo{tier.recommended ? ' (Recommended)' : ''}</option>
                  ))}
                </select>
              </label>
              <div style={s.sccPlanTotal}>
                <span style={s.billingMgmtLabel}>New monthly total</span>
                <span style={s.billingMgmtValue}>${sccMonthlyTotal(sccStaffLimit, sccCloudTier)} USD</span>
              </div>
              <button style={{ ...s.billingMgmtBtn, opacity: sccPlanSaving ? 0.6 : 1 }} onClick={handleSccPlanChange} disabled={sccPlanSaving}>
                {sccPlanSaving ? 'Updating...' : 'Update SCC Plan'}
              </button>
            </div>
            <p style={s.sccPlanNote}>Workforce extensions change total staff allowance only - no features are unlocked. Stripe applies the configured proration and preserves your existing base subscription Price.</p>
            {sccPlanMessage && <div style={s.sccPlanMessage}>{sccPlanMessage}</div>}
          </div>
        )}

        {/* SUBSCRIPTIONS, REFUNDS & CANCELLATION -- jurisdiction-resolved, server-authoritative.
            Placed with Subscription Management so the terms sit next to the thing they govern. */}
        <CommercePolicyPanel />

        {isHobbyPack && (
          <div style={s.hobbyNotice}>
            <span style={s.hobbyNoticeIcon}>i</span>
            <div>
              <strong>Content Pack restrictions:</strong> Additional credits are not available on content packs.
              Credits never expire and do not reset monthly. To get monthly credits, upgrade to a subscription plan.
            </div>
            <button style={s.hobbyNoticeBtn} onClick={() => setActiveTab('upgrade-plan')}>
              View VISINVOKE Plans
            </button>
          </div>
        )}

        <div style={s.tabBar}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              style={{ ...s.tab, ...(activeTab === tab.id ? s.tabActive : {}) }}
              onClick={() => {
                setActiveTab(tab.id);
                setPlanMessage(''); setActionMessage(''); setProfileError(''); setProfileSuccess('');
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ACCOUNT TAB */}
        {activeTab === 'account' && (
          <div style={s.card}>
            <h3 style={s.cardHeading}>Account Details</h3>
            <div style={s.accountEmailRow}>
              <span style={s.accountEmailLabel}>Email Address</span>
              <span style={s.accountEmailValue}>{user?.email}</span>
              <span style={s.accountEmailNote}>Email cannot be changed. Contact support if you need to update it.</span>
            </div>
            <div style={s.profileDivider} />
            {profileError   && <div style={s.profileError}>{profileError}</div>}
            {profileSuccess && <div style={s.profileSuccess}>{profileSuccess}</div>}
            <div style={s.profileGrid}>
              <div style={s.fieldGroup}>
                <label style={s.fieldLabel}>First Name <span style={s.required}>*</span></label>
                <input type="text" style={s.fieldInput} value={profileForm.first_name}
                  onChange={e => { setProfileForm(f => ({ ...f, first_name: e.target.value })); setProfileSuccess(''); }}
                  placeholder="First name" maxLength={128} />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.fieldLabel}>Last Name <span style={s.required}>*</span></label>
                <input type="text" style={s.fieldInput} value={profileForm.last_name}
                  onChange={e => { setProfileForm(f => ({ ...f, last_name: e.target.value })); setProfileSuccess(''); }}
                  placeholder="Last name" maxLength={128} />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.fieldLabel}>Company <span style={s.optional}>(optional)</span></label>
                <input type="text" style={s.fieldInput} value={profileForm.company}
                  onChange={e => { setProfileForm(f => ({ ...f, company: e.target.value })); setProfileSuccess(''); }}
                  placeholder="Company or studio name" maxLength={255} />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.fieldLabel}>Phone <span style={s.optional}>(optional)</span></label>
                <input type="tel" style={s.fieldInput} value={profileForm.phone}
                  onChange={e => { setProfileForm(f => ({ ...f, phone: e.target.value })); setProfileSuccess(''); }}
                  placeholder="Phone number" maxLength={64} />
              </div>
              <div style={{ ...s.fieldGroup, gridColumn: '1 / -1' }}>
                <label style={s.fieldLabel}>Country</label>
                <select style={s.fieldSelect} value={profileForm.country}
                  onChange={e => { setProfileForm(f => ({ ...f, country: e.target.value })); setProfileSuccess(''); }}>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={s.profileActions}>
              <button style={{ ...s.saveBtn, opacity: profileLoading ? 0.6 : 1 }}
                onClick={handleProfileSave} disabled={profileLoading}>
                {profileLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            <div style={s.profileDivider} />
            {!isHobbyPack && !isExplorer && (
              <div style={s.autoTopupBox}>
                <div style={s.autoTopupHeader}>
                  <div>
                    <div style={s.autoTopupTitle}>Auto Additional Credits</div>
                    <div style={s.autoTopupSub}>
                      When your credits reach 0, automatically purchase additional credits if none have been used this cycle.
                    </div>
                  </div>
                  <label style={s.toggleWrap}>
                    <input type="checkbox" style={{ display: 'none' }} checked={autoTopupEnabled}
                      onChange={e => setAutoTopupEnabled(e.target.checked)} />
                    <div style={{ ...s.toggleTrack, background: autoTopupEnabled ? '#8b5cf6' : '#d1d5db' }}>
                      <div style={{ ...s.toggleThumb, transform: autoTopupEnabled ? 'translateX(22px)' : 'translateX(2px)' }} />
                    </div>
                  </label>
                </div>
                {autoTopupEnabled && (
                  <div style={s.autoTopupPicker}>
                    <label style={s.fieldLabel}>Auto additional credits pack</label>
                    <select style={s.fieldSelect} value={autoTopupPack} onChange={e => setAutoTopupPack(e.target.value)}>
                      {ADDITIONAL_CREDITS_INFO.map(p => (
                        <option key={p.key} value={p.key}>+{p.specs} credits — {p.price}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button style={{ ...s.saveBtn, opacity: autoTopupSaving ? 0.6 : 1, marginTop: 8 }}
                    onClick={saveAutoTopup} disabled={autoTopupSaving}>
                    {autoTopupSaving ? 'Saving...' : 'Save Preference'}
                  </button>
                  {autoTopupMsg && <span style={{ fontSize: 13, color: '#10b981' }}>{autoTopupMsg}</span>}
                </div>
              </div>
            )}
            <div style={s.profileDivider} />
            <div style={s.accountMeta}>
              <div style={s.metaItem}>
                <span style={s.metaLabel}>Member Since</span>
                <span style={s.metaValue}>
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' })
                    : '-'}
                </span>
              </div>
              <div style={s.metaItem}>
                <span style={s.metaLabel}>Account ID</span>
                <span style={{ ...s.metaValue, fontFamily: 'monospace', fontSize: 12, color: '#9ca3af' }}>
                  {user?.id?.slice(0, 16)}...
                </span>
              </div>
            </div>
          </div>
        )}

        {/* TECHNICIAN TAB */}
        {activeTab === 'technician' && (
          <TechnicianTab />
        )}

        {/* PACK DETAILS TAB */}
        {activeTab === 'pack-details' && isHobbyPack && (
          <div style={s.card}>
            <h3 style={s.cardHeading}>Content Pack Details</h3>
            <div style={s.packDetailsGrid}>
              <div style={s.packDetailItem}>
                <span style={s.packDetailLabel}>Pack Name</span>
                <span style={s.packDetailValue}>{hobbyPack.pack_name} Content Pack</span>
              </div>
              <div style={s.packDetailItem}>
                <span style={s.packDetailLabel}>Credits Purchased</span>
                <span style={s.packDetailValue}>{hobbyPack.specs_purchased}</span>
              </div>
              <div style={s.packDetailItem}>
                <span style={s.packDetailLabel}>Credits Used</span>
                <span style={s.packDetailValue}>{hobbyPack.specs_used}</span>
              </div>
              <div style={s.packDetailItem}>
                <span style={s.packDetailLabel}>Credits Remaining</span>
                <span style={{ ...s.packDetailValue, color: barColor, fontWeight: 800 }}>{hobbyPack.specs_remaining}</span>
              </div>
              <div style={s.packDetailItem}>
                <span style={s.packDetailLabel}>Purchase Date</span>
                <span style={s.packDetailValue}>
                  {hobbyPack.purchased_at
                    ? new Date(hobbyPack.purchased_at).toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' })
                    : '-'}
                </span>
              </div>
              <div style={s.packDetailItem}>
                <span style={s.packDetailLabel}>Expiry</span>
                <span style={s.packDetailValue}>Never - use at your own pace</span>
              </div>
            </div>
            <div style={s.packRestrictions}>
              <div style={s.restrictionTitle}>Content Pack Restrictions</div>
              <div style={s.restrictionRow}><span style={s.restrictionX}>x</span><span>Additional credits not available</span></div>
              <div style={s.restrictionRow}><span style={s.restrictionX}>x</span><span>No monthly credit reset</span></div>
              <div style={s.restrictionRow}><span style={s.restrictionX}>x</span><span>Cannot purchase another pack while this one is active</span></div>
              <div style={{ ...s.restrictionRow, color: '#16a34a' }}><span style={{ ...s.restrictionX, color: '#16a34a' }}>✓</span><span>Access to all live VISINVOKE systems</span></div>
              <div style={{ ...s.restrictionRow, color: '#16a34a' }}><span style={{ ...s.restrictionX, color: '#16a34a' }}>✓</span><span>Credits never expire</span></div>
              <div style={{ ...s.restrictionRow, color: '#16a34a' }}><span style={{ ...s.restrictionX, color: '#16a34a' }}>✓</span><span>Can upgrade to subscription at any time</span></div>
            </div>
          </div>
        )}

        {/* UPGRADE-PLAN TAB (content pack users) */}
        {activeTab === 'upgrade-plan' && isHobbyPack && (
          <div>
            <div style={s.infoBox}>
              Upgrading to a VISINVOKE plan will give you monthly credits that reset each period.
              Your remaining content pack credits ({hobbyPack.specs_remaining}) will stay available until used.
            </div>
            {planMessage && <div style={s.infoBox}>{planMessage}</div>}
            <div style={s.planGrid}>
              {ALL_PLANS.filter(p => !p.isExplorer).map(plan => {
                const color = PLAN_COLORS[plan.slug] || '#8b5cf6';
                return (
                  <div key={plan.slug} style={s.planCard}>
                    <div style={{ ...s.planDot, background: color }} />
                    <div style={s.planCardName}>{plan.name}</div>
                    <div style={s.planCardSpecs}>{plan.specs} credits / month</div>
                    {plan.specDiff && <div style={s.planCardSpecDiff}>{plan.specDiff}</div>}
                    <div style={s.planCardDesc}>{plan.description}</div>
                    {plan.benefit && <div style={s.planCardBenefit}>{plan.benefit}</div>}
                    <button
                      style={{
                        ...s.planBtn,
                        background: changingPlan === plan.slug ? '#a78bfa' : `linear-gradient(135deg, ${color}, ${color}cc)`,
                        color: '#fff', cursor: 'pointer',
                        opacity: changingPlan === plan.slug ? 0.6 : 1,
                      }}
                      disabled={!!changingPlan}
                      onClick={() => handleChangePlan(plan, billingTerm)}
                    >
                      {changingPlan === plan.slug ? 'Loading...' : `Subscribe to ${plan.name}`}
                    </button>
                  </div>
                );
              })}
            </div>
            <div style={s.subscriptionValueMsg}>
              VISINVOKE plans provide the best value for regular creators. Upgrade or purchase additional credits at any time.
            </div>
          </div>
        )}

        {/* CHANGE PLAN TAB */}
        {activeTab === 'change-plan' && !isHobbyPack && (
          <div>
            <div style={s.termSelectorWrap}>
              <div style={s.termLabel}>Billing Period</div>
              <div style={s.termSelector}>
                {BILLING_TERMS.map(t => (
                  <button
                    key={t.key}
                    style={{ ...s.termBtn, ...(billingTerm === t.key ? s.termBtnActive : {}) }}
                    onClick={() => setBillingTerm(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {progressPct >= 100 && nextPlanInfo && (
              <div style={s.upgradePromptBanner}>
                <span>You have used all your credits this period.</span>
                <button style={s.upgradePromptBtn} onClick={() => handleChangePlan(nextPlanInfo, billingTerm)}>
                  Upgrade to {nextPlanInfo.name} ({nextPlanInfo.specs} credits/mo)
                </button>
              </div>
            )}

            {planMessage && <div style={s.infoBox}>{planMessage}</div>}

            <div style={s.planGrid}>
              {ALL_PLANS.map(plan => {
                const isCurrent = plan.slug === usage?.plan_slug;
                const color     = PLAN_COLORS[plan.slug] || '#8b5cf6';
                const priceStr  = plan.isExplorer ? '' : (PLAN_PRICES[plan.slug]?.[billingTerm] || '');
                return (
                  <div key={plan.slug} style={{ ...s.planCard, ...(isCurrent ? { border: `2px solid ${color}`, boxShadow: `0 0 0 4px ${color}22` } : {}) }}>
                    {isCurrent && <div style={{ ...s.currentBadge, background: color }}>Current Plan</div>}
                    <div style={{ ...s.planDot, background: color }} />
                    <div style={s.planCardName}>{plan.name}</div>
                    {!plan.isExplorer && (
                      <div style={s.planCardPrice}>
                        {priceStr}
                        <span style={s.planCardPer}>
                          {billingTerm === 'monthly' ? '/mo' : billingTerm === '6month' ? ' / 6 mo' : ' / yr'}
                        </span>
                      </div>
                    )}
                    {plan.isExplorer
                      ? <div style={s.planCardSpecs}>10 credits total</div>
                      : <div style={s.planCardSpecs}>{plan.specs} credits / month</div>}
                    {plan.specDiff && <div style={s.planCardSpecDiff}>{plan.specDiff}</div>}
                    <div style={s.planCardDesc}>{plan.description}</div>
                    {plan.isExplorer && (
                      <div style={s.trialReassurance}>One-time allocation. No monthly reset.</div>
                    )}
                    {plan.benefit && <div style={s.planCardBenefit}>{plan.benefit}</div>}
                    <button
                      style={{
                        ...s.planBtn,
                        background: isCurrent ? '#f3f4f6' : `linear-gradient(135deg, ${color}, ${color}cc)`,
                        color: isCurrent ? '#9ca3af' : '#fff',
                        cursor: isCurrent ? 'default' : 'pointer',
                        opacity: changingPlan === plan.slug ? 0.6 : 1,
                      }}
                      disabled={isCurrent || !!changingPlan}
                      onClick={() => handleChangePlan(plan, billingTerm)}
                    >
                      {isCurrent ? 'Current Plan' : changingPlan === plan.slug ? 'Loading...' : `Switch to ${plan.name}`}
                    </button>
                  </div>
                );
              })}
            </div>
            <div style={s.subscriptionValueMsg}>
              VISINVOKE plans provide the best value for regular creators. Upgrade your plan or purchase additional credits at any time.
            </div>

            {usage?.subscription_status === 'active' && usage?.plan_slug !== 'explorer' && (
              <div style={s.cancelSection}>
                <h3 style={s.cancelTitle}>Cancel Subscription</h3>
                <p style={s.cancelText}>
                  Cancelling will stop future billing. You'll retain full access until the end of your current billing period.
                </p>
                {actionMessage && <div style={s.infoBox}>{actionMessage}</div>}
                <button style={s.cancelBtn} onClick={handleCancel} disabled={actionLoading}>
                  {actionLoading ? 'Cancelling...' : 'Cancel Subscription'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ADDITIONAL CREDITS TAB */}
        {activeTab === 'top-up' && !isHobbyPack && (
          <div style={s.card}>
            {progressPct >= 100 && nextPlanInfo && (
              <div style={s.upgradePromptBanner}>
                <span>You have used all your credits this period.</span>
                <button style={s.upgradePromptBtn} onClick={() => { setActiveTab('change-plan'); }}>
                  Upgrade Plan — {nextPlanInfo.name} ({nextPlanInfo.specs} credits/mo)
                </button>
              </div>
            )}
            <TopupSection onPurchaseComplete={() => { loadUsage(); }} />
          </div>
        )}

        {/* USAGE HISTORY TAB */}
        {activeTab === 'usage-history' && (
          <div style={s.card}>
            <h3 style={s.cardHeading}>Usage History</h3>
            {loadingHistory ? (
              <div style={s.emptyState}>Loading...</div>
            ) : usageHistory.length === 0 ? (
              <div style={s.emptyState}>No usage recorded yet. Credits are consumed when you generate images inside any VISINVOKE system.</div>
            ) : (
              <>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Date</th>
                      <th style={s.th}>Generator</th>
                      <th style={s.th}>Category</th>
                      <th style={s.th}>Images</th>
                      <th style={s.th}>Credits</th>
                      <th style={s.th}>Source</th>
                      <th style={s.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageHistory.map((log, i) => (
                      <tr key={log.id || i} style={s.tr}>
                        <td style={{ ...s.td, color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {log.created_at ? new Date(log.created_at).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td style={s.td}>{log.generator_name || '-'}</td>
                        <td style={{ ...s.td, color: '#6b7280' }}>{log.generator_category || '-'}</td>
                        <td style={{ ...s.td, textAlign: 'center', fontWeight: 700 }}>{log.image_count || 1}</td>
                        <td style={{ ...s.td, textAlign: 'center', fontWeight: 700, color: '#7c3aed' }}>{log.credits_used || 1}</td>
                        <td style={s.td}>
                          <span style={{
                            ...s.badge,
                            background: log.credit_source === 'topup' ? '#ede9fe' : log.credit_source === 'hobby_pack' ? '#fef3c7' : log.credit_source === 'trial' ? '#f0fdf4' : '#e0f2fe',
                            color: log.credit_source === 'topup' ? '#7c3aed' : log.credit_source === 'hobby_pack' ? '#92400e' : log.credit_source === 'trial' ? '#166534' : '#0369a1',
                          }}>
                            {log.credit_source === 'topup' ? 'Additional Credits' : log.credit_source === 'hobby_pack' ? 'Content Pack' : log.credit_source === 'trial' ? 'Trial' : 'Subscription'}
                          </span>
                        </td>
                        <td style={s.td}>
                          <span style={{
                            ...s.badge,
                            background: log.job_status === 'success' ? '#d1fae5' : '#fee2e2',
                            color: log.job_status === 'success' ? '#065f46' : '#991b1b',
                          }}>
                            {log.job_status === 'success' ? 'Success' : log.job_status === 'failed' ? 'Failed' : 'Success'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {historyPages > 1 && (
                  <div style={s.pagination}>
                    <button style={{ ...s.pageBtn, opacity: historyPage <= 1 ? 0.4 : 1 }} disabled={historyPage <= 1} onClick={() => loadHistory(historyPage - 1)}>Prev</button>
                    <span style={s.pageInfo}>Page {historyPage} of {historyPages} ({historyTotal} total)</span>
                    <button style={{ ...s.pageBtn, opacity: historyPage >= historyPages ? 0.4 : 1 }} disabled={historyPage >= historyPages} onClick={() => loadHistory(historyPage + 1)}>Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* BILLING HISTORY TAB */}
        {activeTab === 'billing-history' && (
          <div style={s.card}>
            <h3 style={s.cardHeading}>Billing History</h3>
            {loadingBilling ? (
              <div style={s.emptyState}>Loading...</div>
            ) : billingHistory.length === 0 ? (
              <div style={s.emptyState}>No billing history recorded yet.</div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>Description</th>
                    <th style={s.th}>Type</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Amount</th>
                    <th style={s.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {billingHistory.map((item, i) => (
                    <tr key={i} style={s.tr}>
                      <td style={{ ...s.td, color: '#6b7280' }}>
                        {item.date ? new Date(item.date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </td>
                      <td style={s.td}>{item.description}</td>
                      <td style={s.td}>
                        <span style={{
                          ...s.badge,
                          background: item.type === 'additional_credits' ? '#ede9fe' : item.type === 'content_pack' ? '#fef3c7' : '#e0f2fe',
                          color: item.type === 'additional_credits' ? '#7c3aed' : item.type === 'content_pack' ? '#92400e' : '#0369a1',
                        }}>
                          {item.type === 'additional_credits' ? 'Additional Credits' : item.type === 'content_pack' ? 'Content Pack' : 'Subscription'}
                        </span>
                      </td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>
                        {item.amount != null ? `$${parseFloat(item.amount).toFixed(2)}` : '-'}
                      </td>
                      <td style={s.td}>
                        <span style={{
                          ...s.badge,
                          background: item.status === 'active' || item.status === 'completed' ? '#d1fae5' : '#fee2e2',
                          color: item.status === 'active' || item.status === 'completed' ? '#065f46' : '#991b1b',
                        }}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)', fontFamily: "'Segoe UI', sans-serif", paddingBottom: 60 },
  container: { maxWidth: 1500, margin: '0 auto', padding: '32px 40px' },
  pageHeader: { marginBottom: 24 },
  pageHeaderTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 },
  pageTitle: { color: '#fff', fontSize: 30, fontWeight: 800, margin: '0 0 6px' },
  pageSub: { color: 'rgba(255,255,255,0.6)', fontSize: 15, margin: 0 },
  summaryStrip: { background: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: '20px 28px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 0, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', flexWrap: 'wrap' },
  summaryItem: { display: 'flex', flexDirection: 'column', gap: 4, padding: '0 24px' },
  summaryLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af' },
  summaryValue: { fontSize: 18, fontWeight: 700, color: '#1a1a1a' },
  summaryDivider: { width: 1, height: 40, background: '#e5e7eb' },
  barWrap: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160, padding: '0 0 0 24px' },
  barTrack: { height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 4, transition: 'width 0.5s ease' },
  barLabel: { fontSize: 12, color: '#9ca3af', textAlign: 'right' },
  hobbyNotice: { background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.4)', borderRadius: 12, padding: '14px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, color: '#fff', fontSize: 14, flexWrap: 'wrap' },
  billingMgmtSection: { background: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: '20px 28px', marginBottom: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  billingMgmtTitle:   { margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.06em' },
  billingMgmtGrid:    { display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap' },
  billingMgmtItem:    { display: 'flex', flexDirection: 'column', gap: 4, padding: '0 24px' },
  billingMgmtLabel:   { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af' },
  billingMgmtValue:   { fontSize: 17, fontWeight: 700, color: '#1a1a1a' },
  billingMgmtDivider: { width: 1, height: 40, background: '#e5e7eb', flexShrink: 0 },
  billingMgmtActions: { display: 'flex', alignItems: 'center', gap: 12, paddingLeft: 24, flexShrink: 0 },
  billingMgmtBtn:     { ...glassSurface('#5B21B6', '#8b5cf6', '#a855f7'), borderRadius: 10, padding: '11px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  billingMgmtErr:     { fontSize: 13, color: '#dc2626' },
  sccPlanGrid:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, alignItems: 'end' },
  sccPlanField:       { display: 'flex', flexDirection: 'column', gap: 7 },
  sccPlanSelect:      { minHeight: 42, border: '1px solid #d1d5db', borderRadius: 9, padding: '8px 10px', background: '#fff', color: '#111827', fontSize: 14 },
  sccPlanTotal:       { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 145 },
  sccPlanNote:        { margin: '14px 0 0', color: '#6b7280', fontSize: 13, lineHeight: 1.5 },
  sccPlanMessage:     { marginTop: 10, color: '#0f766e', fontSize: 13, fontWeight: 700 },
  hobbyNoticeIcon: { fontSize: 18, flexShrink: 0 },
  hobbyNoticeBtn: { ...glassSurface('#A66D05', '#D4AF37', '#F0D174'), borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 'auto' },
  tabBar: { display: 'flex', gap: 4, marginBottom: 24, background: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: 4 },
  tab: { flex: 1, padding: '12px 8px', border: 'none', cursor: 'pointer', borderRadius: 8, fontSize: 13, fontWeight: 600, background: 'transparent', color: 'rgba(255,255,255,0.6)', transition: 'all 0.2s' },
  tabActive: { background: '#fff', color: '#1a1a1a', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
  termSelectorWrap: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 },
  termLabel: { color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 600 },
  termSelector: { display: 'flex', background: 'rgba(30,16,60,0.7)', border: '1px solid rgba(139,92,246,0.45)', borderRadius: 10, padding: 3, gap: 3 },
  termBtn: { ...glassSurface('#334155', '#64748B', '#94A3B8'), padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s' },
  termBtnActive: { ...glassSurface('#5B21B6', '#8b5cf6', '#a855f7'), fontWeight: 700 },
  upgradePromptBanner: { background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)', borderRadius: 12, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, color: '#fff', fontSize: 14, flexWrap: 'wrap' },
  upgradePromptBtn: { ...glassSurface('#991B1B', '#dc2626', '#F87171'), borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  accountEmailRow: { display: 'flex', flexDirection: 'column', gap: 4, background: '#f9fafb', borderRadius: 10, padding: '14px 16px' },
  accountEmailLabel: { fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' },
  accountEmailValue: { fontSize: 15, fontWeight: 700, color: '#1f2937' },
  accountEmailNote: { fontSize: 12, color: '#9ca3af', marginTop: 2 },
  profileDivider: { height: 1, background: '#f0f0f0', margin: '4px 0' },
  profileError: { background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  profileSuccess: { background: '#dcfce7', border: '1px solid #bbf7d0', color: '#166534', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  profileGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel: { fontSize: 13, fontWeight: 600, color: '#374151' },
  required: { color: '#dc2626' },
  optional: { color: '#9ca3af', fontWeight: 400 },
  fieldInput: { border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', color: '#1a1a1a', background: '#fff' },
  fieldSelect: { border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', color: '#1a1a1a', background: '#fff', cursor: 'pointer' },
  profileActions: { display: 'flex', justifyContent: 'flex-end', marginTop: 20 },
  saveBtn: { background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 28px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  autoTopupBox: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 },
  autoTopupHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 },
  autoTopupTitle: { fontWeight: 700, fontSize: 15, color: '#1f2937', marginBottom: 4 },
  autoTopupSub: { fontSize: 13, color: '#6b7280', lineHeight: 1.5 },
  autoTopupPicker: { display: 'flex', flexDirection: 'column', gap: 6 },
  toggleWrap: { cursor: 'pointer', flexShrink: 0 },
  toggleTrack: { width: 46, height: 26, borderRadius: 13, position: 'relative', transition: 'background 0.2s' },
  toggleThumb: { position: 'absolute', top: 2, width: 22, height: 22, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'transform 0.2s' },
  accountMeta: { display: 'flex', gap: 32, flexWrap: 'wrap' },
  metaItem: { display: 'flex', flexDirection: 'column', gap: 4 },
  metaLabel: { fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' },
  metaValue: { fontSize: 14, fontWeight: 600, color: '#374151' },
  packDetailsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 },
  packDetailItem: { background: '#f9fafb', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 },
  packDetailLabel: { fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' },
  packDetailValue: { fontSize: 16, fontWeight: 700, color: '#1f2937' },
  packRestrictions: { background: '#f9fafb', borderRadius: 10, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 },
  restrictionTitle: { fontWeight: 700, fontSize: 13, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  restrictionRow: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#6b7280' },
  restrictionX: { fontWeight: 800, fontSize: 13, color: '#dc2626', flexShrink: 0, width: 16 },
  planGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 },
  planCard: { background: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: 22, position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: 8, border: '2px solid transparent' },
  currentBadge: { position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap' },
  planDot: { width: 12, height: 12, borderRadius: '50%' },
  planCardName: { fontWeight: 800, fontSize: 17, color: '#1a1a1a' },
  planCardPrice: { fontSize: 20, fontWeight: 800, color: '#1a1a1a', display: 'flex', alignItems: 'baseline', gap: 2 },
  planCardPer: { fontSize: 12, color: '#9ca3af', fontWeight: 500 },
  planCardSpecs: { fontSize: 13, fontWeight: 700, color: '#374151' },
  planCardSpecDiff: { fontSize: 11, color: '#6b7280', fontWeight: 600, marginTop: -4 },
  planCardDesc: { fontSize: 12, color: '#6b7280', lineHeight: 1.5, flexGrow: 1 },
  planCardBenefit: { fontSize: 11, color: '#374151', fontWeight: 600, background: '#f3f4f6', borderRadius: 6, padding: '3px 8px', display: 'inline-block' },
  trialReassurance: { fontSize: 11, color: '#10b981', fontWeight: 600, fontStyle: 'italic' },
  planBtn: { marginTop: 8, border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 700, fontSize: 13, transition: 'opacity 0.2s' },
  subscriptionValueMsg: { textAlign: 'center', color: 'rgba(255,255,255,0.75)', fontSize: 13, fontWeight: 500, margin: '0 0 24px', background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 20px', border: '1px solid rgba(255,255,255,0.15)' },
  cancelSection: { background: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: 28, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' },
  cancelTitle: { margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#1f2937' },
  cancelText: { fontSize: 14, color: '#6b7280', marginBottom: 16, lineHeight: 1.6 },
  cancelBtn: { background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  infoBox: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 14 },
  card: { background: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  cardHeading: { margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.06em' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { textAlign: 'left', padding: '10px 12px', color: '#6b7280', fontWeight: 700, borderBottom: '2px solid #e5e7eb', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px', color: '#1f2937', fontWeight: 500, verticalAlign: 'middle' },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 },
  emptyState: { textAlign: 'center', padding: '40px 20px', color: '#9ca3af', fontSize: 14, lineHeight: 1.6 },
  pagination: { display: 'flex', gap: 12, marginTop: 20, alignItems: 'center', justifyContent: 'center' },
  pageBtn: { padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#1f2937', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  pageInfo: { fontSize: 13, color: '#6b7280' },
};

const hpm = {
  heading: { fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 0, marginBottom: 8, display: 'block' },
  body:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, margin: '0 0 10px 0' },
  list:    { margin: 0, padding: '0 0 4px 16px', display: 'flex', flexDirection: 'column', gap: 0 },
  item:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, marginBottom: 6, borderRadius: 6, padding: '2px 4px' },
  bold:    { fontWeight: 700, color: 'rgba(255,255,255,0.88)' },
  divider: { height: 1, background: 'rgba(255,255,255,0.05)', margin: '12px 0' },
};


// ---------------------------------------------------------------------------
// TechnicianTab
// "Technician" tab for the Manage account hub. Account/access area ONLY:
//   1. Technician account / access status panel
//   2. Structured technician access-request form (shown only when no
//      application/account exists)
//   3. A small Contact Support CTA that opens the existing bottom-right Support
//      modal with a technician category preselected.
//
// This tab intentionally contains NO support message form and NO message inbox.
// All technician questions go through the existing Support modal. Talks to the
// user-facing backend via the shared axios API instance (cookie auth). No fetch,
// no localStorage. The frontend never decides approval: it only displays backend
// status and submits an application.
// ---------------------------------------------------------------------------

const TECH_STATUS_COLORS = {
  pending:            '#6b7280',
  under_review:       '#8b5cf6',
  more_info_required: '#f97316',
  more_information_required: '#f97316',
  approved:           '#0ea5e9',
  active:             '#10b981',
  paused:             '#f59e0b',
  suspended:          '#dc2626',
  cancelled:          '#6b7280',
  rejected:           '#dc2626',
  banned:             '#7f1d1d',
  expired_review:     '#6b7280',
};

// Preselected Support-modal category for technician questions. Must match the
// single technician SUBJECT in components/SupportModal.js.
const TECH_SUPPORT_SUBJECT = 'Technician Account Question';

// Short user-facing copy per technician status. Handles both the backend key
// (more_info_required) and the spec alias (more_information_required).
const TECH_STATUS_COPY = {
  pending:                  'Your technician account is pending review.',
  under_review:             'Your technician account is under review.',
  more_info_required:       'VISINVOKE needs more information about your technician account.',
  more_information_required:'VISINVOKE needs more information about your technician account.',
  approved:                 'Your technician account has been approved.',
  active:                   'Your technician account is active.',
  paused:                   'Your technician account is currently paused.',
  suspended:                'Your technician account is currently suspended.',
  cancelled:                'This technician account has been cancelled.',
  rejected:                 'Your technician account request was not approved.',
  banned:                   'Technician access is unavailable for this account.',
  expired_review:           'This technician review has expired.',
};

// Statuses where a Contact Support action is offered.
const TECH_STATUS_NEEDS_SUPPORT = [
  'more_info_required', 'more_information_required', 'paused', 'suspended',
  'rejected', 'banned', 'cancelled', 'expired_review',
];

// User-facing labels for technician access/capability flags. A card is shown
// only when the corresponding key is actually present in the backend payload.
const TECH_ACCESS_LABELS = {
  desktop_command_access:     'Desktop Command Access',
  hub_access:                 'Hub Access',
  utilities_access:           'Utilities Access',
  software_directory_access:  'Software Directory Access',
  tool_launcher_access:       'Tool Launcher Access',
  job_hub_access:             'Job Hub Access',
  technician_pro_visible:     'Technician Pro Visibility',
  technician_pro_eligible:    'Technician Pro Eligibility',
  advanced_downloads_access:  'Advanced Downloads Access',
  beta_tools_access:          'Beta Tools Access',
  support_priority:           'Support Priority',
};

// User-facing labels for the VISINVOKE Tech Activity Calendar. Event-type and
// source labels describe the ACTION taken inside VISINVOKE (a link clicked, a
// tool selected or opened). They never assert an outcome on a device: nothing
// is claimed installed, fixed, removed, or checked. The system records the
// VISINVOKE Tech action only.
const TECH_EVENT_TYPE_LABELS = {
  DOWNLOAD_LINK_CLICKED:      'Download link clicked',
  DESKTOP_LAUNCHER_ITEM_USED: 'Desktop Launcher item used',
  UTILITY_USED:               'Utility opened',
  REGISTRY_FILE_USED:         'Registry file selected',
  DIAGNOSTIC_TOOL_USED:       'Diagnostic tool selected',
  REPAIR_TOOL_USED:           'Repair tool selected',
  SCRIPT_USED:                'Script selected',
  PACKAGE_USED:               'Package selected',
  DOCUMENT_OPENED:            'Document opened',
  OTHER_TECH_ACTION:          'Other VISINVOKE Tech action',
};

const TECH_SOURCE_LABELS = {
  desktop_launcher:     'Desktop Launcher',
  tech_utilities_page:  'Tech Utilities',
  free_utilities_page:  'Free Utilities',
  technician_dashboard: 'Technician Dashboard',
  download_page:        'Downloads',
  registry_files_page:  'Registry Files',
  diagnostics_page:     'Diagnostics',
  repair_tools_page:    'Repair Tools',
  script_page:          'Scripts',
  package_page:         'Packages',
  other:                'Other',
};

// Backend count-bucket keys (from get_technician_activity_calendar). Used for
// compact day-cell counts and the filter row. Never invent new keys here.
const TECH_BUCKET_LABELS = {
  downloads:        'Downloads',
  desktop_launcher: 'Launcher',
  utilities:        'Utilities',
  registry_files:   'Registry',
  diagnostics:      'Diagnostics',
  repair_tools:     'Repair',
  scripts:          'Scripts',
  packages:         'Packages',
  documents:        'Documents',
  other:            'Other',
};

// item_type -> bucket key, mirroring the backend so a single returned event can
// be filtered client-side without changing backend vocabulary.
const TECH_ITEM_TYPE_BUCKET = {
  download:              'downloads',
  desktop_launcher_tool: 'desktop_launcher',
  utility:               'utilities',
  registry_file:         'registry_files',
  diagnostic:            'diagnostics',
  repair_tool:           'repair_tools',
  script:                'scripts',
  package:               'packages',
  document:              'documents',
  other:                 'other',
};

// Filter row: 'all' plus one entry per backend bucket key.
const TECH_ACTIVITY_FILTERS = [
  { key: 'all',              label: 'All' },
  { key: 'downloads',        label: 'Downloads' },
  { key: 'desktop_launcher', label: 'Desktop Launcher' },
  { key: 'utilities',        label: 'Utilities' },
  { key: 'registry_files',   label: 'Registry Files' },
  { key: 'diagnostics',      label: 'Diagnostics' },
  { key: 'repair_tools',     label: 'Repair Tools' },
  { key: 'scripts',          label: 'Scripts' },
  { key: 'packages',         label: 'Packages' },
  { key: 'documents',        label: 'Documents' },
  { key: 'other',            label: 'Other' },
];

const TECH_WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function techPad2(n) { return n < 10 ? '0' + n : '' + n; }

function techEventBucket(ev) {
  return TECH_ITEM_TYPE_BUCKET[ev && ev.item_type] || 'other';
}

function TechnicianTab() {
  const navigate = useNavigate();
  const [app,        setApp]        = useState(null);
  const [appLoading, setAppLoading] = useState(true);
  const [appErr,     setAppErr]     = useState('');

  // Canonical Technician Application route — the SAME experience reached from the
  // Utilities gold box and the DCC Technician panel. One account, one application.
  const TECH_APPLY_ROUTE = '/dashboard/technician/apply';

  const loadApplication = useCallback(() => {
    setAppLoading(true);
    setAppErr('');
    API.get('/v1/technician-application/me')
      .then(r => setApp(r.data?.application || null))
      .catch(() => setAppErr('Unable to load your technician application status.'))
      .finally(() => setAppLoading(false));
  }, []);

  useEffect(() => { loadApplication(); }, [loadApplication]);

  // --- VISINVOKE Tech Activity Calendar state ---
  const [activityMonth,   setActivityMonth]   = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + techPad2(d.getMonth() + 1);
  });
  const [activity,        setActivity]        = useState(null);   // {month, days:[...]}
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityErr,     setActivityErr]     = useState('');
  const [selectedDay,     setSelectedDay]     = useState('');     // 'YYYY-MM-DD'
  const [activityFilter,  setActivityFilter]  = useState('all');

  const loadActivity = useCallback(() => {
    setActivityLoading(true);
    setActivityErr('');
    const tz = new Date().getTimezoneOffset();
    API.get('/v1/technician-application/activity?month=' + activityMonth + '&tz_offset=' + tz)
      .then(r => {
        const data = (r && r.data) ? r.data : { month: activityMonth, days: [] };
        setActivity(data);
        setSelectedDay(data.days && data.days.length ? data.days[0].date : '');
      })
      .catch(() => {
        // Activity is non-critical: fail soft, never break the tab.
        setActivityErr('Unable to load your activity right now.');
        setActivity(null);
        setSelectedDay('');
      })
      .finally(() => setActivityLoading(false));
  }, [activityMonth]);

  // Load only for a technician account, and whenever the month changes. Depends
  // on the account object and the memoized loader (which changes with the
  // selected month), so there is no unstable-dependency refetch loop.
  useEffect(() => {
    if (app) { loadActivity(); }
  }, [app, loadActivity]);

  const shiftActivityMonth = (delta) => {
    const parts = activityMonth.split('-');
    let y = parseInt(parts[0], 10);
    let m = parseInt(parts[1], 10) + delta;
    if (m < 1)  { m = 12; y -= 1; }
    if (m > 12) { m = 1;  y += 1; }
    setActivityMonth(y + '-' + techPad2(m));
  };

  const openSupport = () => {
    // Open the existing Support modal with the technician category preselected.
    // No fallback form is created here by design.
    try {
      window.dispatchEvent(new CustomEvent('vi:open-support', { detail: { subject: TECH_SUPPORT_SUBJECT } }));
    } catch (e) { /* no-op */ }
  };

  const statusColor = app ? (TECH_STATUS_COLORS[app.status] || '#6b7280') : '#6b7280';
  const fmtDay = (iso) => iso ? new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' }) : '-';

  // Technician access summary source of truth: effective_capabilities preferred,
  // otherwise capabilities. Only render cards for keys actually returned.
  const accessSource = app ? (app.effective_capabilities || app.capabilities || null) : null;
  const accessKeys = accessSource
    ? Object.keys(TECH_ACCESS_LABELS).filter(k => Object.prototype.hasOwnProperty.call(accessSource, k))
    : [];

  const statusKey  = app ? (app.status || '') : '';
  const statusCopy = TECH_STATUS_COPY[statusKey] || app?.status_message || '';
  const needsSupport = TECH_STATUS_NEEDS_SUPPORT.includes(statusKey);

  // --- Calendar render derivation (local values only; no hooks) ---
  const amParts  = activityMonth.split('-');
  const calYear  = parseInt(amParts[0], 10);
  const calMonth = parseInt(amParts[1], 10);   // 1-12
  const monthLabel = new Date(calYear, calMonth - 1, 1)
    .toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

  const dayMap = {};
  if (activity && activity.days) {
    activity.days.forEach(d => { dayMap[d.date] = d; });
  }

  const daysInMonth  = new Date(calYear, calMonth, 0).getDate();
  const firstWeekday = (new Date(calYear, calMonth - 1, 1).getDay() + 6) % 7; // Monday-first

  const calCells = [];
  let ci;
  for (ci = 0; ci < firstWeekday; ci++) { calCells.push(null); }
  for (ci = 1; ci <= daysInMonth; ci++) {
    const dateKey = calYear + '-' + techPad2(calMonth) + '-' + techPad2(ci);
    calCells.push({ day: ci, date: dateKey, data: dayMap[dateKey] || null });
  }
  while (calCells.length % 7 !== 0) { calCells.push(null); }

  const cellCount = (dayData) => {
    if (!dayData || !dayData.counts) { return 0; }
    if (activityFilter === 'all') { return dayData.counts.total || 0; }
    return dayData.counts[activityFilter] || 0;
  };

  const selectedData = selectedDay ? (dayMap[selectedDay] || null) : null;
  const selectedEvents = (selectedData && selectedData.events)
    ? selectedData.events.filter(ev => {
        if (activityFilter === 'all') { return true; }
        return techEventBucket(ev) === activityFilter;
      })
    : [];
  const selectedDayLabel = selectedDay
    ? new Date(selectedDay + 'T00:00:00')
        .toLocaleDateString('en-AU', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
    : '';
  const hasMonthActivity = !!(activity && activity.days && activity.days.length > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ---------- TECHNICIAN ACCESS STATUS (read-only) ---------- */}
      <div style={s.card}>
        <h3 style={s.cardHeading}>Technician Access</h3>

        {appLoading && <div style={s.emptyState}>Loading your technician status...</div>}
        {!appLoading && appErr && <div style={s.profileError}>{appErr}</div>}

        {/* No technician application yet -> APPLY (canonical application) */}
        {!appLoading && !appErr && !app && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={ts.statusMessage}>You don’t have a VISINVOKE Technician application yet.</div>
            <div style={ts.note}>
              VISINVOKE Technician provides professional tools for verified technicians and technical service
              businesses. Access is granted after a manual VISINVOKE review of your application.
            </div>
            <div>
              <button style={ts.applyBtn} onClick={() => navigate(TECH_APPLY_ROUTE)}>Apply for Technician Access</button>
            </div>
          </div>
        )}

        {/* Existing application / account: read-only status + user-safe info */}
        {!appLoading && !appErr && app && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={ts.statusRow}>
              <span style={{ ...s.badge, background: statusColor + '22', color: statusColor, border: '1px solid ' + statusColor + '55' }}>
                {(statusKey || '').replace(/_/g, ' ').toUpperCase()}
              </span>
              {statusCopy && <span style={ts.statusMessage}>{statusCopy}</span>}
            </div>
            {(app.status === 'draft' || app.status === 'more_info_required') && (
              <div>
                <button style={ts.applyBtn} onClick={() => navigate(TECH_APPLY_ROUTE)}>
                  {app.status === 'draft' ? 'Continue Application' : 'Update Application'}
                </button>
              </div>
            )}
            {app.status === 'active' && (
              <div style={ts.note}>
                Your Technician access is active. Open the Desktop Command Centre to get or launch VISINVOKE Technician.
              </div>
            )}
            <div style={s.accountMeta}>
              <div style={s.metaItem}>
                <span style={s.metaLabel}>Business</span>
                <span style={s.metaValue}>{app.business_name || '-'}</span>
              </div>
              {app.trading_name && (
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Trading Name</span>
                  <span style={s.metaValue}>{app.trading_name}</span>
                </div>
              )}
              {app.technician_type && (
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Technician Type</span>
                  <span style={s.metaValue}>{app.technician_type}</span>
                </div>
              )}
              <div style={s.metaItem}>
                <span style={s.metaLabel}>Country</span>
                <span style={s.metaValue}>{app.country || '-'}</span>
              </div>
              {app.verification_status && (
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Verification</span>
                  <span style={s.metaValue}>{String(app.verification_status).replace(/_/g, ' ')}</span>
                </div>
              )}
              {app.technician_pro && (app.technician_pro.status || app.technician_pro.active !== undefined) && (
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Technician Pro</span>
                  <span style={s.metaValue}>{app.technician_pro.active ? 'Active' : (app.technician_pro.status || 'Inactive')}</span>
                </div>
              )}
              <div style={s.metaItem}>
                <span style={s.metaLabel}>Submitted</span>
                <span style={s.metaValue}>{fmtDay(app.submitted_at || app.created_at)}</span>
              </div>
              {app.updated_at && (
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Last Updated</span>
                  <span style={s.metaValue}>{fmtDay(app.updated_at)}</span>
                </div>
              )}
              {app.last_reviewed_at && (
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Last Reviewed</span>
                  <span style={s.metaValue}>{fmtDay(app.last_reviewed_at)}</span>
                </div>
              )}
            </div>
            {needsSupport && (
              <div>
                <button style={ts.ctaBtn} onClick={openSupport}>Contact Support</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---------- TECHNICIAN ACCESS SUMMARY (backend-driven only) ---------- */}
      {!appLoading && !appErr && app && (
        <div style={s.card}>
          <h3 style={s.cardHeading}>Technician Access Summary</h3>
          {accessKeys.length === 0 ? (
            <div style={ts.note}>No technician access details are available yet.</div>
          ) : (
            <div style={ts.accessGrid}>
              {accessKeys.map(k => {
                const on = !!accessSource[k];
                return (
                  <div key={k} style={ts.accessCard}>
                    <span style={ts.accessLabel}>{TECH_ACCESS_LABELS[k]}</span>
                    <span style={{ ...ts.accessState, color: on ? '#059669' : '#9ca3af' }}>
                      {on ? 'Enabled' : 'Not enabled'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ---------- VISINVOKE TECH ACTIVITY CALENDAR ---------- */}
      {!appLoading && !appErr && app && (
        <div style={s.card}>
          <div style={ts.calHeader}>
            <h3 style={{ ...s.cardHeading, margin: 0 }}>VISINVOKE Tech Activity</h3>
            <div style={ts.calNav}>
              <button style={ts.calNavBtn} onClick={() => shiftActivityMonth(-1)} aria-label="Previous month">{'\u2039'}</button>
              <span style={ts.calMonthLabel}>{monthLabel}</span>
              <button style={ts.calNavBtn} onClick={() => shiftActivityMonth(1)} aria-label="Next month">{'\u203A'}</button>
            </div>
          </div>

          <div style={ts.filterRow}>
            {TECH_ACTIVITY_FILTERS.map(f => {
              const on = activityFilter === f.key;
              return (
                <button
                  key={f.key}
                  style={{ ...ts.filterPill, ...(on ? ts.filterPillOn : null) }}
                  onClick={() => setActivityFilter(f.key)}
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          {activityLoading && <div style={ts.note}>Loading your activity...</div>}
          {!activityLoading && activityErr && <div style={ts.note}>{activityErr}</div>}

          {!activityLoading && !activityErr && (
            <div>
              <div style={ts.weekHeaderRow}>
                {TECH_WEEKDAY_HEADERS.map(w => (
                  <div key={w} style={ts.weekHeaderCell}>{w}</div>
                ))}
              </div>
              <div style={ts.calGrid}>
                {calCells.map((cell, idx) => {
                  if (cell === null) {
                    return <div key={'b' + idx} style={ts.calCellEmpty} />;
                  }
                  const count = cellCount(cell.data);
                  const isSelected = selectedDay === cell.date;
                  const hasActivity = !!(cell.data && cell.data.counts && (cell.data.counts.total || 0) > 0);
                  return (
                    <button
                      key={cell.date}
                      style={{
                        ...ts.calCell,
                        ...(hasActivity ? ts.calCellActive : null),
                        ...(isSelected ? ts.calCellSelected : null),
                      }}
                      onClick={() => setSelectedDay(cell.date)}
                    >
                      <span style={ts.calDayNum}>{cell.day}</span>
                      {count > 0 && <span style={ts.calCountPill}>{count}</span>}
                    </button>
                  );
                })}
              </div>

              {!hasMonthActivity && (
                <div style={{ ...ts.note, marginTop: 16 }}>No VISINVOKE Tech activity in {monthLabel}.</div>
              )}

              {hasMonthActivity && (
                <div style={ts.selectedDayWrap}>
                  <div style={ts.selectedDayHeader}>
                    {selectedDay ? selectedDayLabel : 'Select a day to see its activity.'}
                  </div>
                  {selectedDay && selectedEvents.length === 0 && (
                    <div style={ts.note}>
                      {activityFilter === 'all'
                        ? 'No activity on this day.'
                        : 'No ' + (TECH_BUCKET_LABELS[activityFilter] || 'matching') + ' activity on this day.'}
                    </div>
                  )}
                  {selectedDay && selectedEvents.length > 0 && (
                    <div style={ts.eventList}>
                      {selectedEvents.map(ev => (
                        <div key={ev.id} style={ts.eventRow}>
                          <span style={ts.eventTime}>{ev.time || '-'}</span>
                          <span style={ts.eventType}>{TECH_EVENT_TYPE_LABELS[ev.event_type] || 'VISINVOKE Tech action'}</span>
                          <span style={ts.eventSource}>{TECH_SOURCE_LABELS[ev.source] || (ev.source || 'VISINVOKE Tech')}</span>
                          <span style={ts.eventItem}>{ev.item_name || '-'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---------- CONTACT SUPPORT CTA ---------- */}
      <div style={ts.ctaCard}>
        <div>
          <div style={ts.ctaTitle}>Need help with your technician access?</div>
          <div style={ts.ctaSub}>Account access, verification, and Technician Pro questions go through Support.</div>
        </div>
        <button style={ts.ctaBtn} onClick={openSupport}>Contact Support</button>
      </div>

    </div>
  );
}

// TechnicianTab-local styles (reuses `s` for shared tokens).
const ts = {
  intro:        { fontSize: 13, color: '#6b7280', lineHeight: 1.6, marginBottom: 4 },
  note:         { fontSize: 13, color: '#6b7280', lineHeight: 1.6, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px' },
  subHeading:   { fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 },
  statusRow:    { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  statusMessage:{ fontSize: 14, color: '#374151', fontWeight: 500 },
  checkRow:     { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: '#374151', lineHeight: 1.5, cursor: 'pointer' },
  ctaCard:      { background: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: '18px 24px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' },
  ctaTitle:     { fontSize: 15, fontWeight: 700, color: '#1f2937' },
  ctaSub:       { fontSize: 13, color: '#6b7280', marginTop: 4, maxWidth: 520, lineHeight: 1.5 },
  ctaBtn:       { background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  applyBtn:     { background: 'linear-gradient(135deg, #D4AF37, #b8942b)', color: '#0a0a0a', border: 'none', borderRadius: 10, padding: '12px 26px', fontWeight: 800, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  accessGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12 },
  accessCard:   { display: 'flex', flexDirection: 'column', gap: 4, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' },
  accessLabel:  { fontSize: 13, fontWeight: 600, color: '#374151' },
  accessState:  { fontSize: 13, fontWeight: 700 },

  // --- VISINVOKE Tech Activity Calendar ---
  calHeader:        { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 },
  calNav:           { display: 'flex', alignItems: 'center', gap: 10 },
  calNavBtn:        { background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, width: 30, height: 30, fontSize: 16, lineHeight: 1, color: '#374151', cursor: 'pointer', fontWeight: 700 },
  calMonthLabel:    { fontSize: 14, fontWeight: 700, color: '#1f2937', minWidth: 150, textAlign: 'center' },
  filterRow:        { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  filterPill:       { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#6b7280', cursor: 'pointer' },
  filterPillOn:     { background: 'rgba(139,92,246,0.12)', border: '1px solid #8b5cf6', color: '#6d28d9' },
  weekHeaderRow:    { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 6 },
  weekHeaderCell:   { textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' },
  calGrid:          { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 },
  calCell:          { position: 'relative', minHeight: 54, background: '#fff', border: '1px solid #eef0f3', borderRadius: 10, padding: '6px 8px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', cursor: 'pointer', font: 'inherit' },
  calCellEmpty:     { minHeight: 54, background: 'transparent', border: '1px solid transparent', borderRadius: 10 },
  calCellActive:    { borderColor: '#d8ccf6', background: 'rgba(139,92,246,0.05)' },
  calCellSelected:  { borderColor: '#8b5cf6', boxShadow: '0 0 0 2px rgba(139,92,246,0.25)' },
  calDayNum:        { fontSize: 13, fontWeight: 600, color: '#374151' },
  calCountPill:     { marginTop: 6, background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#fff', borderRadius: 999, fontSize: 11, fontWeight: 700, padding: '1px 8px', minWidth: 18, textAlign: 'center' },
  selectedDayWrap:  { marginTop: 18, borderTop: '1px solid #eef0f3', paddingTop: 16 },
  selectedDayHeader:{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 },
  eventList:        { display: 'flex', flexDirection: 'column', gap: 8 },
  eventRow:         { display: 'grid', gridTemplateColumns: '80px 1fr', gap: '2px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px' },
  eventTime:        { gridColumn: '1', gridRow: '1 / span 3', fontSize: 13, fontWeight: 700, color: '#6d28d9', alignSelf: 'center' },
  eventType:        { gridColumn: '2', gridRow: '1', fontSize: 13, fontWeight: 600, color: '#1f2937' },
  eventSource:      { gridColumn: '2', gridRow: '2', fontSize: 12, color: '#9ca3af' },
  eventItem:        { gridColumn: '2', gridRow: '3', fontSize: 13, color: '#374151' },
};
